import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select as MuiSelect,
  TextField,
  Typography,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppTheme } from "@/utils/theme";
import { useTranslation } from "react-i18next";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useGetUserPrograms } from "@/hooks/usePrograms";
import { ContextualAgentService } from "@/services/contextual-agent.service";
import { usePermissionAlert } from "@/hooks/usePermissionAlert";

interface ExportConfig {
  programId: string;
}

const ExportPage: React.FC = () => {
  const { setPageTitle } = usePageStore();
  const { userInfo, hasSystemAdminRole, userHasAccessToCode } = useUserStore();
  const { t } = useTranslation(["clients", "common"]);
  const { showSnackBar } = useFeedBack();

  const { programs, isLoadingPrograms } = useGetUserPrograms({
    userId: userInfo?._id as string,
    hasSystemAdminRole,
  });

  const programsOptions = useMemo(() => {
    if (!programs) return [];
    return programs.map((program) => ({
      key: program._id,
      value: program.name,
    }));
  }, [programs]);

  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    programId: "",
  });

  const [isExporting, setIsExporting] = useState(false);
  const { showPermissionAlert, PermissionAlertModal } =
    usePermissionAlert("clients");

  useEffect(() => {
    setPageTitle(t("common:export.title", "Export Data"));
  }, [setPageTitle, t]);

  const handleExport = async () => {
    if (!exportConfig.programId) {
      showSnackBar("Please select a program", "error");
      return;
    }

    setIsExporting(true);
    try {
      const result = await ContextualAgentService.exportExcel(exportConfig.programId);
      if (result?.downloadUrl) {
        const link = document.createElement("a");
        link.href = result.downloadUrl;
        link.download = result.fileName || "export.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSnackBar("Export started. Check your downloads.", "success");
      } else {
        showSnackBar("No download URL returned.", "error");
      }
      setExportConfig({
        ...exportConfig,
        programId: "",
      });
    } catch (error) {
      showSnackBar("Export failed. Please try again.", "error");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="headlineSmall" component="h1">
            Export CA Short Questionnaires 
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0}  sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="titleMedium" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: "bold" }}>
                Export
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="program-label">Program</InputLabel>
                    <MuiSelect
                      labelId="program-label"
                      value={exportConfig.programId}
                      label="Program"
                      onChange={(e) =>
                        setExportConfig({
                          ...exportConfig,
                          programId: e.target.value,
                        })
                      }
                      disabled={isLoadingPrograms}
                    >
                      {programsOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.value}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={
                    userHasAccessToCode("EXPORT_CA_SHORT_QUESTIONNAIRE")
                      ? handleExport
                      : showPermissionAlert
                  }
                  disabled={isExporting || !exportConfig.programId}
                  size="large"
                >
                  {isExporting ? "Exporting..." : "Export Excel"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <PermissionAlertModal />

    </Box>
  );
};

export default ExportPage;
